/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* global globalThis */
import { useState, useEffect, useCallback, useRef } from "react";

import { Box, CssBaseline, Button } from "@mui/material";

import { styled } from "@mui/material/styles";

import CommonSearchBar from "../components/common/searchBar/CommonSearchBar";

import CommonTable from "../components/common/table/commonTable";

import PageHeader from "../components/common/PageHeader";
import { getManualTasksColumns } from "../components/common/table/manualTasksColumns";
import "./members.css";

import DeleteTaskModal from "../components/modals/DeleteTaskModal";
import NewTaskModal from "../components/modals/NewTaskModal";
import CompleteTaskModal from "../components/modals/CompleteTaskModal";
import EditTaskModal from "../components/modals/EditTaskModal";
import SuccessPopup from "../components/modals/SuccessPopup";
import { usePermissions } from "../redux/permissions";

import _ from "lodash";
import {
  fetchManualTasks,
  completeManualTask,
  createManualTask,
  updateManualTask,
  deleteManualTask,
} from "../api/manualTask";
import { useLoading } from "../context/LoadingContext";

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const MainContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  flexGrow: 1,
  padding: 0,
  margin: 0,
  width: open ? "calc(100% - 240px)" : "100%",
  marginLeft: open ? "240px" : "0",
  height: "calc(100vh - 50px)",
  backgroundColor: "#F7F8FB",
  overflow: "hidden",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const ManualTasksPage = () => {
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const pageParam = params.get("page");
    const taskIdParam = params.get("taskId");
    if (pageParam !== null && !Number.isNaN(Number(pageParam))) {
      setPageIndex(Number(pageParam));
      setCurrentPage(Number(pageParam));
    }
    if (taskIdParam) {
      setHighlightedTaskId(taskIdParam);
    }
  }, []);
  const { canWrite, canDelete } = usePermissions("Manual Tasks");

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize] = useState(30);
  const tableRef = useRef(null);

  // Lazy loading refs
  const isFetchingMoreRef = useRef(false);
  const fetchSeqRef = useRef(0);
  const lastFetchSignatureRef = useRef(null);
  const filterChangeRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [mobileOpen] = useState(false);

  const [isDeleteTaskModalOpen, setIsDeleteTaskModalOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [isCompleteTaskModalOpen, setIsCompleteTaskModalOpen] = useState(false);
  const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successRecipientType, setSuccessRecipientType] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const pageParam = params.get("page");
    if (pageParam !== null && !Number.isNaN(Number(pageParam))) {
      setPageIndex(Number(pageParam));
      setCurrentPage(Number(pageParam));
    }
  }, []);

  const [memberIds] = useState([]);

  const searchRef = useRef(null);
  const isSearchTriggered = useRef(false);
  const [isResetSearch, setIsResetSearch] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [selectedProviderNpis] = useState([]);
  const [selectedPharmacyNpis] = useState([]);
  const [selectedProgramIds] = useState([]);

  const { showLoading, hideLoading } = useLoading();

  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [containerScale, setContainerScale] = useState(1);

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);

  const fetchData = useCallback(
    async (page = 0, size = 30) => {
      try {
        const mySeq = ++fetchSeqRef.current;
        setLoading(true);
        if (page > 0) {
          isFetchingMoreRef.current = true;
        }
        showLoading();
        setError(null);

        const requestBody = {
          memberIds: memberIds,
          providerNpis: selectedProviderNpis,
          pharmacyNpis: selectedPharmacyNpis,
          programIds: selectedProgramIds,
        };

        const signature = JSON.stringify({
          page: Number(page),
          size: Number(size),
          search: searchText,
          body: requestBody,
        });

        if (page > 0 && lastFetchSignatureRef.current === signature) {
          setLoading(false);
          hideLoading();
          isFetchingMoreRef.current = false;
          return;
        }
        lastFetchSignatureRef.current = signature;

        const response = await fetchManualTasks({
          page: Number(page),
          size: Number(size),
          search: searchText,
          body: requestBody,
        });

        if (mySeq !== fetchSeqRef.current) {
          return;
        }

        const normalized = {
          content: response.content || [],
          totalElements: response.totalElements || 0,
        };

        if (page === 0) {
          setData(normalized.content);
        } else {
          setData((prev) => [...prev, ...normalized.content]);
        }

        setTotalRows((prev) => {
          const returnedTotal = normalized.totalElements;
          const returnedCount = normalized.content.length;
          const currentTotalContent =
            page === 0 ? returnedCount : (prev || 0) + returnedCount;

          if (returnedTotal === returnedCount && returnedCount === size) {
            return currentTotalContent + 1;
          }

          return returnedTotal || currentTotalContent;
        });

        setLoading(false);
        hideLoading();
      } catch (err) {
        console.error("Failed to fetch manual tasks:", err);
        setError("Failed to fetch manual tasks");
        setLoading(false);
        hideLoading();
      } finally {
        isFetchingMoreRef.current = false;
        filterChangeRef.current = false;
        hideLoading();
      }
    },
    [
      searchText,
      memberIds,
      selectedProviderNpis,
      selectedPharmacyNpis,
      selectedProgramIds,
      showLoading,
      hideLoading,
    ],
  );

  useEffect(() => {
    fetchData(pageIndex, pageSize);
  }, [pageIndex, pageSize, fetchData]);

  useEffect(() => {
    filterChangeRef.current = true;
    setData([]);
    setTotalRows(0);
    isFetchingMoreRef.current = false;
    setPageIndex(0);
    lastFetchSignatureRef.current = null;
  }, [
    searchText,
    memberIds,
    selectedProviderNpis,
    selectedPharmacyNpis,
    selectedProgramIds,
  ]);

  useEffect(() => {
    const el = tableRef.current?.scrollElement;
    if (!el) return;

    const onScroll = () => {
      if (filterChangeRef.current || loading || isFetchingMoreRef.current)
        return;
      const canLoadMore = (data?.length || 0) < (totalRows || 0);
      if (!canLoadMore) return;
      const threshold = 200;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
        isFetchingMoreRef.current = true;
        setPageIndex((p) => p + 1);
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [loading, data.length, totalRows]);

  const handleQuickView = (row) => {};

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      showLoading();

      await deleteManualTask(selectedRows);

      setData((prevData) =>
        prevData.filter((task) => !selectedRows.includes(task.id)),
      );

      setSelectedRows([]);
      setIsDeleteTaskModalOpen(false);
      setPageIndex(0);
      setTotalRows(0);
      await fetchData(0, pageSize);
    } catch (error) {
      setError("Failed to delete tasks", error);
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  const handleCompleteTask = (task) => {
    setSelectedTask(task);
    setIsCompleteTaskModalOpen(true);
  };

  const handleSaveCompleteTask = async (taskData, markAsComplete) => {
    try {
      setLoading(true);
      showLoading();

      const completeTaskPayload = [
        {
          manualTaskId: selectedTask.id,
          outReachMethod: taskData.outreachReason,
          dispositionOutcome: taskData.outreachOutcome || "",
          dispositionStatus: taskData.status
            ? taskData.status.replaceAll(/\s+/g, "_").toUpperCase()
            : "",
          dispositionNote: taskData.notes,
          recipientType:
            selectedTask.recipientType ||
            selectedTask.recipient?.type ||
            "member",
          status: markAsComplete ? "COMPLETED" : "CREATED",
        },
      ];

      await completeManualTask(completeTaskPayload);

      setData((prevData) =>
        prevData.filter((task) => task.id !== selectedTask.id),
      );

      setSelectedRows((prevSelected) =>
        prevSelected.filter((id) => id !== selectedTask.id),
      );

      setIsCompleteTaskModalOpen(false);
      setLoading(false);
      hideLoading();
      setPageIndex(0);
      setTotalRows(0);
      fetchData(0, pageSize);
    } catch (error) {
      setError("Failed to complete task", error);
      setLoading(false);
      hideLoading();
    } finally {
      hideLoading();
    }
  };

  const handleSearch = useCallback(
    _.debounce((text) => {
      setSearchText(text);
      isSearchTriggered.current = true;
      setPageIndex(0);
      setCurrentPage(0);
      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    [],
  );

  const handleEditTask = (task) => {
    const timezoneMap = {
      EST: "ET",
      EDT: "ET",
      CST: "CT",
      CDT: "CT",
      MST: "MT",
      MDT: "MT",
      PST: "PT",
      PDT: "PT",
      AKST: "AKT",
      AKDT: "AKT",
      HST: "HST",
      HDT: "HST",
    };

    const rawTimezone =
      task.timezone || task.timeZone || task.recipient?.timezone || "N/A";
    const mappedTimezone =
      timezoneMap[rawTimezone.toUpperCase()] || rawTimezone;

    const formattedTask = {
      ...task,
      recipientName: task.recipient?.name || task.recipientName || "",
      recipientType: (
        task.recipientType ||
        task.recipient?.type ||
        "member"
      ).toLowerCase(),
      recipientId: task.recipient?.id || task.recipientId || "",
      timeZone: mappedTimezone,
      language: task.language || task.recipient?.language || "N/A",
      details: task.taskNote || task.note || task.details || "",
      outreachReason: task.outreachReason || "",

      dueDate: task.dueDate ? new Date(task.dueDate) : new Date(),
      linkedMember: task.linkedMemberId || task.linkedMember || "",
      linkedMemberName: task?.linkedMember,
      program: task.programId || task.program || "",
      recentActivityStatus: task.recentActivity?.status || "",
      recentActivityAt: task.recentActivity?.timestamp || "",
    };

    setSelectedTask(formattedTask);
    setIsEditTaskModalOpen(true);
  };

  const handleSaveNewTask = async (newTask) => {
    try {
      setLoading(true);

      const createTaskPayload = {
        recipientType: newTask.recipientType?.toUpperCase() || "MEMBER",

        programId: Number.parseInt(newTask.program, 10) || 1,
        outreachReason:
          newTask.outreachReason?.toUpperCase()?.replaceAll(/\s+/g, "_") ||
          "PROVIDER_CONSULTATION",
        note: newTask.details || "",
        dueDate: newTask.dueDate,
      };

      if (newTask.recipientType?.toLowerCase() !== "others") {
        createTaskPayload.recipientId = Number.parseInt(
          newTask.recipientId,
          10,
        );
      }
      if (newTask.recipientType?.toLowerCase() === "others") {
        createTaskPayload.timezone = newTask.timeZone || "";
        createTaskPayload.language = newTask.language || "";
        createTaskPayload.otherRecipientName = newTask.recipientName || "";
      }

      if (
        (newTask.recipientType === "provider" ||
          newTask.recipientType === "pharmacy") &&
        newTask.linkedMember
      ) {
        createTaskPayload.linkedMemberId = Number.parseInt(
          newTask.linkedMember,
          10,
        );
      }

      await createManualTask(createTaskPayload);

      const isOutreachTask = ["provider", "pharmacy"].includes(
        newTask.recipientType?.toLowerCase(),
      );
      setPageIndex(0);
      setTotalRows(0);
      lastFetchSignatureRef.current = null;
      await fetchData(0, pageSize);

      if (isOutreachTask) {
        setSuccessRecipientType(newTask.recipientType);
        setShowSuccessPopup(true);
      }

      setIsNewTaskModalOpen(false);
      setLoading(false);
    } catch (error) {
      setError(error.message || "Failed to create new task");
      setLoading(false);
    }
  };

  const [isUpdatingTask, setIsUpdatingTask] = useState(false);

  const handleSaveEditTask = async (updatedTask) => {
    if (!selectedTask) return;

    setIsUpdatingTask(true);
    try {
      const taskData = {
        dueDate: updatedTask.dueDate,
        outreachReason: updatedTask.outreachReason,
        note: updatedTask.details || "",
      };

      await updateManualTask(selectedTask.id, taskData);

      setData([]);
      setTotalRows(0);
      setPageIndex(0);
      setCurrentPage(0);
      lastFetchSignatureRef.current = null;
      filterChangeRef.current = true;
      isFetchingMoreRef.current = false;

      await fetchData(0, pageSize);

      setIsEditTaskModalOpen(false);
      setSelectedTask(null);
    } catch (error) {
      setError(
        error.message || "Failed to update task",
        currentPage,
        isUpdatingTask,
      );
    } finally {
      setIsUpdatingTask(false);
    }
  };

  const columns = getManualTasksColumns(
    handleQuickView,
    handleCompleteTask,
    handleEditTask,
    { canWrite },
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "transparent",
      }}
    >
      <CssBaseline />

      <MainContent sx={{ width: "0px" }} open={mobileOpen}>
        <div
          style={{
            transform: `scale(${containerScale})`,
            transformOrigin: "top left",
            width: containerScale < 1 ? "111%" : "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              height: "100%",
              position: "relative",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
                "& .page-header": {
                  margin: 0,
                  padding: 0,
                  "& h2": {
                    margin: 0,
                    lineHeight: "36px",
                  },
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <PageHeader title="Manual Tasks" />
              </Box>
            </Box>

            <NewTaskModal
              open={isNewTaskModalOpen}
              onClose={() => setIsNewTaskModalOpen(false)}
              onSave={handleSaveNewTask}
            />
            <CompleteTaskModal
              open={isCompleteTaskModalOpen}
              onClose={() => setIsCompleteTaskModalOpen(false)}
              onSave={handleSaveCompleteTask}
              task={selectedTask}
            />
            <DeleteTaskModal
              open={isDeleteTaskModalOpen}
              onClose={() => setIsDeleteTaskModalOpen(false)}
              onConfirm={handleConfirmDelete}
            />
            <EditTaskModal
              open={isEditTaskModalOpen}
              onClose={() => {
                setIsEditTaskModalOpen(false);
                setSelectedTask(null);
              }}
              task={selectedTask}
              onSave={handleSaveEditTask}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "0 0 -4px 0",
                width: "100%",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <CommonSearchBar
                    onSearch={handleSearch}
                    isReset={isResetSearch}
                    ref={searchRef}
                    placeholder="Search recipients, task notes..."
                  />
                </div>

                {selectedRows.length > 0 && (
                  <div>
                    <span
                      id="delete-task-description"
                      style={{ display: "none" }}
                    >
                      Delete {selectedRows.length} selected task
                      {selectedRows.length > 1 ? "s" : ""}
                    </span>
                    <Button
                      variant="contained"
                      onClick={() =>
                        canDelete && setIsDeleteTaskModalOpen(true)
                      }
                      aria-describedby="delete-task-description"
                      aria-label={`Delete Task: ${
                        selectedRows.length
                      } selected task${selectedRows.length > 1 ? "s" : ""}`}
                      sx={{
                        minWidth: "100px",
                        height: "32px",
                        backgroundColor: "transparent",
                        color: canDelete ? "#F15B59" : "#888888",
                        border: canDelete
                          ? "1px solid #F15B59"
                          : "1px solid #CCCCCC",
                        textTransform: "none",
                        borderRadius: "16px",
                        fontSize: "13px",
                        fontWeight: 500,
                        padding: "4px 12px",
                        "&:hover": {
                          backgroundColor: "transparent",
                          borderColor: canDelete ? "#E53935" : "#CCCCCC",
                          color: canDelete ? "#E53935" : "#888888",
                        },
                        boxShadow: "none",
                        opacity: canDelete ? 1 : 0.5,
                        cursor: canDelete ? "pointer" : "not-allowed",
                        whiteSpace: "nowrap",
                      }}
                      disabled={!canDelete}
                    >
                      Delete Task
                    </Button>
                  </div>
                )}
              </div>

              <div style={{ flexShrink: 0 }}>
                <Button
                  variant="contained"
                  onClick={() => canWrite && setIsNewTaskModalOpen(true)}
                  aria-label="Create new task"
                  sx={{
                    height: "37px",
                    minWidth: "100px",
                    px: 2,
                    borderRadius: "18px",
                    textTransform: "none",
                    fontSize: "16px",
                    fontWeight: 400,
                    fontFamily: "Inter, sans-serif",
                    backgroundColor: canWrite ? "#403C6F" : "#CFCEDA",
                    color: canWrite ? "white" : "#666666",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: canWrite ? "#34305A" : "#CFCEDA",
                      boxShadow: "none",
                    },
                    opacity: canWrite ? 1 : 0.7,
                    cursor: canWrite ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap",
                  }}
                  disabled={!canWrite}
                >
                  New Task
                </Button>
              </div>
            </Box>

            <div
              className="search-results-container"
              style={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
                marginTop: "12px",
              }}
            >
              <section
                aria-label="Manual tasks table"
                style={{
                  flexGrow: 1,
                  overflowY: "auto",
                }}
              >
                {error && <p style={{ color: "red" }}>{error}</p>}
                {!error && (
                  <CommonTable
                    ref={tableRef}
                    columns={columns}
                    data={data}
                    loading={loading}
                    enableRowSelection={true}
                    onRowSelectionChange={handleRowSelection}
                    selectedRows={selectedRows}
                    paginate={false}
                    source="manualTasks"
                    highlightedTaskId={highlightedTaskId}
                    tableHeight="calc(100vh - 120px)"
                  />
                )}
              </section>
            </div>
          </Box>
        </div>
      </MainContent>
      <SuccessPopup
        open={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
        }}
        recipientType={successRecipientType}
      />
    </Box>
  );
};

export default ManualTasksPage;
